/**
 * Draggable Component List
 * Renders a draggable list of polls and text fields
 * Uses react-beautiful-dnd for drag and drop functionality
 */

import React from "react";
import { DragDropContext, Draggable, DropResult } from "react-beautiful-dnd";
import StrictModeDroppable from "@Components/StrictModeDroppable";

export interface ComponentItem {
    id: string;
    type: "poll" | "textfield";
    title: string;
    question?: string;
}

export interface DraggableComponentListProps {
    items: ComponentItem[];
    onReorder: (reorderedItems: ComponentItem[]) => void;
}

export const DraggableComponentList: React.FC<DraggableComponentListProps> = ({
    items,
    onReorder,
}) => {
    const handleDragEnd = (result: DropResult) => {
        const { source, destination } = result;

        // If dropped outside the list, do nothing
        if (!destination) {
            return;
        }

        // If dropped in the same position, do nothing
        if (
            source.droppableId === destination.droppableId &&
            source.index === destination.index
        ) {
            return;
        }

        // Create a new array with the item moved
        const newItems = Array.from(items);
        const [movedItem] = newItems.splice(source.index, 1);
        newItems.splice(destination.index, 0, movedItem);

        // Call the callback with the reordered items
        onReorder(newItems);
    };

    if (items.length === 0) {
        return (
            <div
                style={{ textAlign: "center", padding: "24px", color: "#999" }}
            >
                <p>No polls or text fields to reorder</p>
            </div>
        );
    }

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <StrictModeDroppable droppableId="components-list">
                {(provided, snapshot) => (
                    <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={{
                            background: snapshot.isDraggingOver
                                ? "#f5f5f5"
                                : "transparent",
                            padding: "8px 0",
                            borderRadius: "8px",
                            transition: "background-color 0.2s",
                        }}
                    >
                        {items.map((item, index) => (
                            <Draggable
                                key={item.id}
                                draggableId={item.id}
                                index={index}
                            >
                                {(provided, snapshot) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={{
                                            padding: "12px",
                                            marginBottom: "8px",
                                            background: "#fff",
                                            borderRadius: "8px",
                                            border: snapshot.isDragging
                                                ? "2px solid #667eea"
                                                : "1px solid #e0e0e0",
                                            boxShadow: snapshot.isDragging
                                                ? "0 4px 12px rgba(102, 126, 234, 0.3)"
                                                : "none",
                                            display: "flex",
                                            alignItems: "center",
                                            cursor: "grab",
                                            ...provided.draggableProps.style,
                                        }}
                                    >
                                        {/* Drag Handle */}
                                        <div
                                            style={{
                                                marginRight: "12px",
                                                fontSize: "18px",
                                                color: "#999",
                                            }}
                                        >
                                            ☰
                                        </div>

                                        {/* Index Badge */}
                                        <div
                                            style={{
                                                width: "32px",
                                                height: "32px",
                                                borderRadius: "50%",
                                                background: "#667eea",
                                                color: "#fff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "14px",
                                                fontWeight: 600,
                                                marginRight: "12px",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {index + 1}
                                        </div>

                                        {/* Item Details */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                    marginBottom: "4px",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: "11px",
                                                        fontWeight: 600,
                                                        padding: "2px 8px",
                                                        borderRadius: "4px",
                                                        background:
                                                            item.type === "poll"
                                                                ? "#e3f2fd"
                                                                : "#f1f8e9",
                                                        color:
                                                            item.type === "poll"
                                                                ? "#1976d2"
                                                                : "#558b2f",
                                                    }}
                                                >
                                                    {item.type === "poll"
                                                        ? "POLL"
                                                        : "TEXT FIELD"}
                                                </span>
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: "14px",
                                                    fontWeight: 500,
                                                    color: "#333",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {item.title}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </StrictModeDroppable>
        </DragDropContext>
    );
};

export default DraggableComponentList;
